import { PageHeader } from '@/components/shared/page-header';
import { StatusBadge } from '@/components/shared/status-badge';
import { formatDateTime } from '@/lib/utils';
import { sortByLocation, type SaleOrderLine } from './sale-order-locations';
import { WAREHOUSE_STATUS_LABELS } from './warehouse-status';
import { isZeroQty, ZERO_QTY_ROW_CLASS } from './zero-qty';
import { ZeroQtyTag } from './zero-qty-tag';

interface OwnerLine extends SaleOrderLine {
  productInStockQty?: number | string | null;
}

/**
 * Read-only PO view for a private warehouse's owner tenant. GET
 * /purchase-orders/:id returns the restricted owner shape (isWarehouseOwnerView)
 * when the PO belongs to another tenant: the owner's own product names, pick
 * locations and stock — no buyer catalogue or vendor fields. The server rejects
 * receive / update from the owner, so nothing here is editable; fulfilment is
 * done from the Warehouse Orders list.
 */
export function WarehouseOwnerPurchaseOrderView({ order }: { order: any }) {
  const items = sortByLocation((order.items ?? []) as OwnerLine[]);

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Purchase Order #${order.orderNumber}`}
        backTo="#/pos-management?tab=warehouse-orders"
        backLabel="Back to warehouse orders"
      />

      <div className="rounded-lg border border-border bg-white p-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
          <div>
            <div className="text-xs text-muted-foreground">Destination</div>
            <div className="mt-1 text-sm font-medium">{order.location?.name || order.locationName || '—'}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Ordered By</div>
            <div className="mt-1 text-sm font-medium">{order.createdByName || 'System'}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Created</div>
            <div className="mt-1 text-sm font-medium">{formatDateTime(order.createdAt)}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Status</div>
            <div className="mt-1">
              <StatusBadge status={order.status} labelMap={WAREHOUSE_STATUS_LABELS} />
            </div>
          </div>
        </div>
        {order.purpose && (
          <div className="mt-4 text-sm">
            <span className="text-muted-foreground">Purpose: </span>
            <span className="font-medium uppercase">{order.purpose}</span>
          </div>
        )}
        {order.notes && (
          <div className="mt-2 whitespace-pre-wrap text-sm">
            <span className="text-muted-foreground">Notes: </span>
            {order.notes}
          </div>
        )}
        <div className="mt-4 rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
          This order was placed by another company. It is read-only here — fulfil it from the Warehouse Orders list.
        </div>
      </div>

      <div className="rounded-lg border border-border bg-white p-6">
        <h3 className="mb-4 text-base font-semibold">Line Items</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs text-muted-foreground">
                <th className="pb-2">Product</th>
                <th className="pb-2">Barcode</th>
                <th className="pb-2">Building</th>
                <th className="pb-2">Aisle</th>
                <th className="pb-2">Bin/Bay</th>
                <th className="pb-2">U/M</th>
                <th className="pb-2 text-right">Qty</th>
                <th className="pb-2 text-right">In Stock</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-6 text-center text-sm text-muted-foreground">
                    No line items.
                  </td>
                </tr>
              )}
              {items.map((it) => (
                <tr
                  key={it.id}
                  className={`border-b border-border ${isZeroQty(it.quantity) ? ZERO_QTY_ROW_CLASS : ''}`}
                >
                  <td className="py-2">
                    {it.productName}
                    {isZeroQty(it.quantity) && <ZeroQtyTag />}
                  </td>
                  <td className="py-2 text-muted-foreground">{it.productBarcode || '—'}</td>
                  <td className="py-2">{it.productBuilding || '—'}</td>
                  <td className="py-2">{it.productAisle || '—'}</td>
                  <td className="py-2">{it.productBin || '—'}</td>
                  <td className="py-2">{it.unitOfMeasure}</td>
                  <td className="py-2 text-right">{it.quantity}</td>
                  <td className="py-2 text-right text-muted-foreground">{Number(it.productInStockQty) || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
